import type {
	API_AddSupportTicketCommentRequest,
	API_AddSupportTicketCommentResponse,
	API_GetSupportTicketResponse,
	API_ListSupportTicketsAdminQuery,
	API_ListSupportTicketsAdminResponse,
	PaginationMeta,
	SupportTicketDetails,
	SupportTicketListItem,
} from "@pilatestime-admin/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

export interface SupportTicketsListData {
	tickets: SupportTicketListItem[];
	pagination: PaginationMeta;
}

/**
 * Fetch support tickets list from the API (admin)
 */
export const fetchSupportTicketsAdmin = (
	token: string,
	params?: API_ListSupportTicketsAdminQuery,
): Promise<SupportTicketsListData> =>
	api
		.get<API_ListSupportTicketsAdminResponse>("/support-tickets", {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params,
		})
		.then((res) => res.data.data);

/**
 * Fetch a single support ticket by ID (admin)
 */
export const fetchSupportTicketAdmin = (
	token: string,
	ticketId: string,
): Promise<SupportTicketDetails> =>
	api
		.get<API_GetSupportTicketResponse>(`/support-tickets/${ticketId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data.data);

/**
 * Add a comment to a support ticket (admin)
 */
export const addSupportTicketCommentAdmin = (
	token: string,
	ticketId: string,
	data: API_AddSupportTicketCommentRequest,
): Promise<API_AddSupportTicketCommentResponse> =>
	api
		.post<API_AddSupportTicketCommentResponse>(
			`/support-tickets/${ticketId}/comments`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

/**
 * Query options for fetching support tickets list (admin)
 */
export const fetchSupportTicketsAdminQueryOptions = (
	token: string,
	params?: API_ListSupportTicketsAdminQuery,
) =>
	queryOptions({
		queryKey: ["support-tickets-admin", params],
		queryFn: () => fetchSupportTicketsAdmin(token, params),
		staleTime: 1 * 60 * 1000, // 1 minute
	});

/**
 * Query options for fetching a single support ticket (admin)
 */
export const fetchSupportTicketAdminQueryOptions = (
	token: string,
	ticketId: string,
) =>
	queryOptions({
		queryKey: ["support-ticket-admin", ticketId],
		queryFn: () => fetchSupportTicketAdmin(token, ticketId),
		staleTime: 1 * 60 * 1000, // 1 minute
	});

/**
 * Mutation hook for adding a comment to a support ticket (admin)
 */
export const useAddSupportTicketCommentAdmin = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			ticketId,
			data,
		}: {
			token: string;
			ticketId: string;
			data: API_AddSupportTicketCommentRequest;
		}) => addSupportTicketCommentAdmin(token, ticketId, data),
		toastMessages: {
			loading: t("supportTickets.addingComment"),
			success: t("supportTickets.commentAdded"),
			error: t("supportTickets.commentError"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate specific ticket to refresh comments
			queryClient.invalidateQueries({
				queryKey: ["support-ticket-admin", variables.ticketId],
			});
			// Also invalidate the list
			queryClient.invalidateQueries({
				queryKey: ["support-tickets-admin"],
			});
		},
	});
};
